import { DialogProps } from "primereact/dialog";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { stack, row } from "@ui/style";

import { useI18n } from "@core/i18n/i18n";
import { useAsyncOperation } from "@core/hooks/use-async-operation";

import { BaseDialog, DIALOG_SIZE } from "@ui/components/dialog/base-dialog";
import { Button } from "@ui/components/buttons/button";
import { CountryLanguageInput } from "@ui/components/inputs/country-language-input/country-language-input";
import { userManager } from "@core/service-container";
import { ToastContext } from "@ui/components/common/toast-provider";

export type EditUserLocaleDialogProps = DialogProps & {
    currentLocale: string;
    currentCountry: string;
    userSupportedLocales: string[];
};

interface FormData {
    newLocale: string;
}

export function EditUserLocaleDialog({
    currentLocale,
    currentCountry,
    userSupportedLocales,
    onHide,
    ...otherProps
}: EditUserLocaleDialogProps): JSX.Element {
    const { format, locale: currentI18nLocale } = useI18n();
    const { showToast } = useContext(ToastContext);
    const { control, handleSubmit, watch } = useForm<FormData>({
        defaultValues: { newLocale: currentLocale },
    });
    
    const newLocale = watch("newLocale");
    const [hasShownToast, setHasShownToast] = useState(false);

    useEffect(() => {
        if (currentI18nLocale === newLocale && newLocale !== currentLocale && !hasShownToast) {
            showToast("", format("settings.accountTab.editLanguageDialog.successToast.detail"), "success");
            setHasShownToast(true);
        }
    }, [currentI18nLocale, newLocale, currentLocale, format, showToast, hasShownToast]);

    const [submitNewLocale, isLoading] = useAsyncOperation(
        useCallback(
            async ({ newLocale: locale }: FormData) => {
                try {
                    await userManager.updateUser({ locale });
                    onHide();
                } catch (error) {
                    console.error("Failed to update locale", error);
                    throw error;
                }
            },
            [onHide],
        ),
        {},
    );

    return (
        <BaseDialog
            closeOnEscape={false}
            header={format("settings.accountTab.editLanguageDialog.header")}
            footer={
                <ButtonContainer>
                    <Button
                        loading={isLoading}
                        type="submit"
                        label={format("global.dialog.save")}
                        onClick={handleSubmit(submitNewLocale)}
                    />
                </ButtonContainer>
            }
            width={DIALOG_SIZE.S}
            onHide={onHide}
            {...otherProps}
        >
            <Form onSubmit={handleSubmit(submitNewLocale)}>
                <ControllersContainer>
                    <Controller
                        name="newLocale"
                        control={control}
                        render={({ field }) => (
                            <StyledCountryLanguageInput
                                id="edit-user-locale-dialog"
                                locales={new Map([[currentCountry, userSupportedLocales]])}
                                placeholder={format("global.form.dropdown.placeholder")}
                                emptyMessage={format("register.countrySelection.countryDropdown.emptyMessage")}
                                {...field}
                            />
                        )}
                    />
                </ControllersContainer>
            </Form>
        </BaseDialog>
    );
}

const Form = styled(motion.form)`
    ${stack("M", "spacebetween", "stretch")};
    height: 100%;
`;

const ControllersContainer = styled.div`
    ${stack("M", "flexstart", "stretch")};
`;

const ButtonContainer = styled.div`
    ${row(24, "flexend")};
`;

const StyledCountryLanguageInput = styled(CountryLanguageInput)`
    margin-top: 8px;
`;